import * as s from "@gromia/components/theme/t.module.css";
import { useHeaderHeight } from "@gromia/components/ui/Header";
import cx from "classnames";
import { Endomorphism } from "fp-ts/lib/function";
import React from "react";

type Options = {
  disabled?: boolean;
  top: number | Endomorphism<number>;
  zIndex?: number;
  className?: string;
};

type Props = {
  className?: string;
  children: (x: { isSticky: boolean }) => React.ReactNode;
};

export const stickyPad: Endomorphism<number> = x => x + 24;

export function useSticky(o: Options) {
  const [isSticky, setIsSticky] = React.useState(false);
  const ref = React.useRef(null);

  React.useLayoutEffect(() => {
    const ssr = typeof window === "undefined";
    const support = !ssr && "IntersectionObserver" in window;

    if (!support) {
      return;
    }

    function callback(xs: IntersectionObserverEntry[]) {
      const x = xs[0];
      setIsSticky(!x.isIntersecting);
    }

    const observer = new IntersectionObserver(callback);
    observer.observe(ref.current!);
    return () => observer.unobserve(ref.current!);
  }, []);

  const h = useHeaderHeight();
  const top = typeof o.top === "function" ? o.top(h) : o.top;

  function render(node: React.ReactNode) {
    return (
      <React.Fragment>
        <div ref={ref} style={{ height: 0, background: "red" }} />
        <div
          className={o.disabled ? "" : cx(s.positionSticky, o.className)}
          style={o.disabled ? {} : { top, zIndex: o.zIndex }}
        >
          {node}
        </div>
      </React.Fragment>
    );
  }

  return {
    isSticky,
    render,
  };
}

export function Sticky(props: Props & Options) {
  const s = useSticky(props);
  return s.render(props.children({ isSticky: s.isSticky }));
}
